// M13.6 → M13.7 → SKE-3450 — AI insights section on /app/report.
//
// Mounts between the DateRangeSelector and the report body (see
// ReportOverview.tsx). Three states: skeleton, populated, error.
// Populated state renders the structured-JSON payload in the "Ledger"
// editorial layout (drawn from schedugram-client's interface-design
// system — editor's desk, Gotham-only, hairline rules, tabular-nums
// metrics, a subtle background tint on the priority-1 row):
//   - "{Weekly|Monthly|Quarterly|AI} briefing — Signal" eyebrow + headline
//     (derived from the report range, so a 30- or 90-day window doesn't
//     claim to be "Weekly")
//   - `summary.previewBullets` numbered 01/02/03, always visible
//   - "See more detail" disclosure revealing `summary.expandedParagraphs`
//   - 3–5 actions as tabular rows with `# · Action · Metric` columns,
//     hairline row dividers, tabular-nums delta in the right column.
//     Actions are never hidden behind the disclosure toggle.
//
// Regenerate button is rate-limited server-side (INSIGHTS_REGEN:
// 20/hour/(user, property)). When the server returns 429 we render a
// retry message inline rather than firing the request again immediately.
//
// Insights always run on `DEFAULT_INSIGHTS_MODEL_ID`; the per-env model
// picker was removed when we settled on a single primary model.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { EASE } from "../motion/tokens";
import { SectionHeader } from "./SectionHeader";
import { InsightsDisclosure } from "./InsightsDisclosure";
import {
  fetchInsights,
  InsightsRateLimitedError,
  InsightsUnavailableError,
  type InsightsAction,
  type InsightsResult,
  type InsightsTelemetry,
  type InsightsV2,
  type ReportCompare,
} from "../../lib/api";
import { dayCount } from "../../../shared/report/date-range";
import { FALLBACK_CHAIN, getModelLatencyStats } from "../../../shared/insights/models";

// SKE-3453 — Skeleton-loading copy, deterministic by elapsed time so
// re-renders don't desync and screenshots reproduce consistently. Four
// phases × 4 seconds each = 16 seconds of distinct copy; after that the
// copy holds on the last entry until the request lands (or the slow
// hint replaces it at p95 × 1.5).
const SKELETON_PHASES = [
  "Reading your traffic mix…",
  "Comparing platforms and channels…",
  "Ranking this week's actions…",
  "Writing the briefing…",
] as const;
const SKELETON_PHASE_MS = 4_000;

function formatElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const mm = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const ss = (seconds % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}

/**
 * Pick the possessive noun that best describes the current report window
 * for headings like "What {periodPossessive} data is telling you".
 *
 * 7 day range  → "this week's"
 * ~28 day      → "this month's"
 * ~90 day      → "this quarter's"
 * anything else → "this period's"
 *
 * Kept as a plain function so the heading stays correct on every
 * range change without piping the label through props.
 */
function periodPossessive(startDate: string, endDate: string): string {
  const days = dayCount(startDate, endDate);
  if (days <= 7) return "this week's";
  if (days <= 32) return "this month's";
  if (days <= 100) return "this quarter's";
  return "this period's";
}

/**
 * Adjective form of the period for editorial eyebrows — e.g. the Ledger's
 * "{Weekly|Monthly|Quarterly|AI} briefing — Signal". Mirrors the bucketing
 * of `periodPossessive` so the two stay in lockstep.
 */
function briefingPeriod(startDate: string, endDate: string): string {
  const days = dayCount(startDate, endDate);
  if (days <= 7) return "Weekly";
  if (days <= 32) return "Monthly";
  if (days <= 100) return "Quarterly";
  return "AI";
}

export interface InsightsSectionProps {
  propertyId: string;
  startDate: string;
  endDate: string;
  compare: ReportCompare;
  compareStart?: string;
  compareEnd?: string;
}

type Status =
  | { kind: "loading" }
  | {
      kind: "ready";
      insights: InsightsResult;
      insightsV2: InsightsV2;
      telemetry: InsightsTelemetry;
    }
  | { kind: "error"; error: InsightsUnavailableError | null; message: string }
  | { kind: "rate_limited"; retryAfterSeconds: number };

export function InsightsSection(props: InsightsSectionProps) {
  const { propertyId, startDate, endDate, compare, compareStart, compareEnd } = props;
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  // Disables the regenerate button for ~1s after a successful response so
  // the user doesn't fire a second request before reading the new copy.
  const [cooldown, setCooldown] = useState(false);
  // Monotonic request id — every load() call bumps it; only the latest
  // call is allowed to land setState. Guards against the in-flight race
  // where the user changes the date window mid-request and the old
  // response would otherwise overwrite the newer state. A simple ref
  // counter is enough; we don't need full AbortController semantics
  // because /api/insights doesn't support request cancellation server-side.
  const requestIdRef = useRef(0);
  // Track the cooldown timeout so we can clear it on unmount and avoid a
  // setState-after-unmount warning.
  const cooldownTimeoutRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  // Per-window auto-retry guard for parse_failure. When the server
  // exhausts its repair passes and still can't produce valid JSON, the
  // client fires one more regenerate before surfacing the error panel —
  // this ref stops that retry from looping if the second attempt also
  // parse-fails. Reset whenever the resolved window changes.
  const autoRetriedRef = useRef(false);
  const autoRetryTimeoutRef = useRef<number | null>(null);

  const load = useCallback(
    async (opts: { regenerate: boolean; escalate?: boolean }) => {
      const { regenerate, escalate = false } = opts;
      const reqId = ++requestIdRef.current;
      setStatus({ kind: "loading" });
      try {
        const response = await fetchInsights({
          propertyId,
          startDate,
          endDate,
          compare,
          ...(compareStart ? { compareStart } : {}),
          ...(compareEnd ? { compareEnd } : {}),
          ...(regenerate ? { regenerate: true } : {}),
          // SKE-3456 — `escalate: true` is set only by the error panel's
          // "Try again" button. On the happy-path Regenerate button this
          // stays false so the cheap-tier FALLBACK_CHAIN keeps serving
          // the idle-refresh case.
          ...(escalate ? { escalate: true } : {}),
        });
        if (!mountedRef.current || reqId !== requestIdRef.current) return;
        setStatus({
          kind: "ready",
          insights: response.insights,
          insightsV2: response.insightsV2,
          telemetry: response.telemetry,
        });
        if (regenerate) {
          setCooldown(true);
          if (cooldownTimeoutRef.current !== null) {
            window.clearTimeout(cooldownTimeoutRef.current);
          }
          cooldownTimeoutRef.current = window.setTimeout(() => {
            cooldownTimeoutRef.current = null;
            if (mountedRef.current) setCooldown(false);
          }, 1000);
        }
      } catch (err) {
        if (!mountedRef.current || reqId !== requestIdRef.current) return;
        if (err instanceof InsightsRateLimitedError) {
          setStatus({ kind: "rate_limited", retryAfterSeconds: err.retryAfterSeconds });
          return;
        }
        // Parse failures slip past the server's 2 repair passes rarely,
        // but when they do one more regenerate is usually enough (fresh
        // temperature, fresh KV context). Retry once, silently, before
        // surfacing the error panel — the skeleton stays up a bit longer
        // on the bad-luck path. Guarded by `autoRetriedRef` so a
        // persistent parse failure doesn't loop.
        //
        // SKE-3471: the retry path must NEVER leave `status` at "loading"
        // if the scheduled retry doesn't land (component unmount, fetch
        // rejection the load() itself doesn't recover from, etc.) —
        // otherwise the Regenerate button stays spinning forever. Two
        // safety nets: (1) the retry's own load() call has its own
        // try/catch that always transitions status, and (2) a hard
        // failsafe timer below that force-flips to `error` if we're
        // somehow still on this request's "loading" after 5s past the
        // 500ms retry delay.
        if (
          err instanceof InsightsUnavailableError &&
          err.reason === "parse_failure" &&
          !autoRetriedRef.current
        ) {
          autoRetriedRef.current = true;
          if (autoRetryTimeoutRef.current !== null) {
            window.clearTimeout(autoRetryTimeoutRef.current);
          }
          const parseFailureForFailsafe = err;
          autoRetryTimeoutRef.current = window.setTimeout(() => {
            autoRetryTimeoutRef.current = null;
            if (mountedRef.current) void load({ regenerate: true });
          }, 500);
          // Failsafe: if 5.5s passes and we're STILL on this request's
          // "loading" (retry never bumped requestIdRef because it
          // couldn't run, or the retry's setStatus races), surface the
          // original parse_failure as a visible error so the user can
          // at least click Try again manually.
          window.setTimeout(() => {
            if (!mountedRef.current) return;
            if (requestIdRef.current !== reqId) return; // retry landed — nothing to do
            setStatus({
              kind: "error",
              error: parseFailureForFailsafe,
              message: parseFailureForFailsafe.message,
            });
          }, 5_500);
          return;
        }
        if (err instanceof InsightsUnavailableError) {
          setStatus({ kind: "error", error: err, message: err.message });
          return;
        }
        // Catch-all for the AbortController timeout (SKE-3471), network
        // errors, JSON parse errors, and any other thrown value — must
        // always transition out of "loading" so Regenerate can be
        // clicked again.
        const message =
          err instanceof DOMException && err.name === "AbortError"
            ? "AI request timed out after 65s. Try again."
            : (err as Error).message || "AI summary temporarily unavailable.";
        setStatus({ kind: "error", error: null, message });
      }
    },
    [propertyId, startDate, endDate, compare, compareStart, compareEnd],
  );

  // Reset the auto-retry guard whenever the resolved window /
  // property changes — each fresh input gets its own retry budget.
  useEffect(() => {
    autoRetriedRef.current = false;
  }, [propertyId, startDate, endDate, compare, compareStart, compareEnd]);

  // Reload when the resolved window or selected model changes — the
  // parent dashboard rebuilds the report on the same window trigger so
  // the two requests fire in parallel and resolve independently. Stale
  // in-flight requests are ignored via the requestIdRef guard inside
  // load().
  //
  // Pass `regenerate: true` so first paint always reflects the latest
  // numbers rather than whatever was cached from the previous session.
  // Server rate-limit (20/hour/(user, property)) is gracefully handled
  // via the `rate_limited` status branch below.
  useEffect(() => {
    void load({ regenerate: true });
  }, [load]);

  // Mount / unmount lifecycle for `mountedRef` + any pending cooldown
  // timer. Important: reset `mountedRef.current = true` on mount, not
  // just false on cleanup. Otherwise React StrictMode's forced
  // mount → cleanup → remount cycle leaves the ref at false across the
  // remount, which causes *every* in-flight /api/insights response to
  // be dropped by the `!mountedRef.current` guard inside load() — the
  // skeleton never clears.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cooldownTimeoutRef.current !== null) {
        window.clearTimeout(cooldownTimeoutRef.current);
        cooldownTimeoutRef.current = null;
      }
      if (autoRetryTimeoutRef.current !== null) {
        window.clearTimeout(autoRetryTimeoutRef.current);
        autoRetryTimeoutRef.current = null;
      }
    };
  }, []);

  // SKE-3453 — skeleton uses the primary FALLBACK_CHAIN model's latency
  // curve. For escalation retries (ESCALATION_CHAIN[0] = gpt-5.4-mini,
  // ~880ms vs grok ~730ms) this under-estimates by ~20%, but the
  // slow-hint threshold still triggers at the right time because it's
  // relative to the picked stats. Post-response telemetry reveals the
  // actual model.
  const activeModelId = FALLBACK_CHAIN[0];

  return (
    <section id="insights" className="scroll-mt-24 space-y-4">
      <div className="flex items-end justify-between gap-4">
        <SectionHeader
          eyebrow="AI insights"
          title={
            <>
              What {periodPossessive(startDate, endDate)} data is{" "}
              <em className="font-serif text-purple-700">telling you</em>
            </>
          }
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void load({ regenerate: true })}
          disabled={status.kind === "loading" || cooldown}
          className="text-purple-700"
        >
          <i
            className={`fa-solid fa-rotate-right text-sm ${
              status.kind === "loading" ? "animate-spin" : ""
            }`}
            aria-hidden="true"
          />
          Regenerate
        </Button>
      </div>

      {/* SKE-3450 — the Ledger renders its own chrome (article + ring),
          so the generic rounded wrapper only wraps the non-ready
          states (loading skeleton, rate-limit, error panel).

          Fade old out then new in (mode="wait" is sequential, not a true
          crossfade; opacity only, ~180ms) when Regenerate produces fresh
          insights, instead of a hard swap. The ready branch keys on the
          headline so a regenerate with the same status.kind="ready" still
          triggers the fade; non-ready states key on status.kind. The
          fa-spin on the button is unchanged. */}
      <AnimatePresence mode="wait" initial={false}>
        {status.kind === "ready" ? (
          <motion.div
            key={`ready:${status.insightsV2.headline}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: EASE }}
          >
            <InsightsLedger
              insightsV2={status.insightsV2}
              startDate={startDate}
              endDate={endDate}
            />
          </motion.div>
        ) : (
          <motion.div
            key={status.kind}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: EASE }}
            className="rounded-2xl bg-white p-6 ring-1 ring-neutral-950/5"
          >
            {/* SKE-3456 — error-panel Try again escalates to the stronger
                ESCALATION_CHAIN. That button is only reachable AFTER a
                failure, so spending the escalated budget is justified —
                the user has signalled they want a better answer, not
                the same one faster. Happy-path Regenerate (the header
                button above) stays on the cheap default path. */}
            {renderBody(
              status,
              () => void load({ regenerate: true, escalate: true }),
              activeModelId,
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// `renderBody` only handles non-ready states — the ready path renders
// `<InsightsLedger>` directly from the caller because the Ledger's chrome
// replaces the generic wrapper.
function renderBody(
  status: Exclude<Status, { kind: "ready" }>,
  onRetry: () => void,
  activeModelId: string,
): JSX.Element {
  if (status.kind === "loading") return <InsightsSkeleton modelId={activeModelId} />;
  if (status.kind === "rate_limited") {
    const minutes = Math.max(1, Math.round(status.retryAfterSeconds / 60));
    return (
      <InsightsErrorPanel
        title="Too many regenerations"
        message={`You've used the regenerate quota for this property. Try again in about ${minutes} minute${
          minutes === 1 ? "" : "s"
        }.`}
        onRetry={null}
      />
    );
  }
  return (
    <InsightsErrorPanel
      title={errorTitleFor(status.error)}
      message={status.message}
      detail={errorDetailFor(status.error)}
      rawPreview={status.error?.rawContentPreview ?? null}
      onRetry={onRetry}
    />
  );
}

// Short user-safe title derived from the server's `reason` code. Keeps
// the header specific (timeout vs. HTTP vs. nothing-parsed) so users
// understand whether a retry might help.
function errorTitleFor(error: InsightsUnavailableError | null): string {
  if (!error) return "AI summary temporarily unavailable";
  switch (error.reason) {
    case "openrouter_transport":
      if (error.transportError === "timeout") return "AI provider took too long to respond";
      if (error.transportError === "abort") return "AI request was aborted";
      return "Couldn't reach the AI provider";
    case "openrouter_http":
      return `AI provider returned an error${error.providerStatus ? ` (HTTP ${error.providerStatus})` : ""}`;
    case "parse_failure":
      return "AI returned nothing usable";
    case "timeout_budget": {
      const budgetSec = error.budgetMs ? Math.round(error.budgetMs / 1000) : 30;
      return `AI summary didn't finish in ${budgetSec}s`;
    }
    case "internal":
      return "Something went wrong while generating insights";
    default:
      return "AI summary temporarily unavailable";
  }
}

// Compact one-line detail for the error panel. Surfaces full internal
// detail (user chose this level during hardening, 2026-04-20) so
// screenshots + bug reports carry enough context to triage.
function errorDetailFor(error: InsightsUnavailableError | null): string | null {
  if (!error) return null;
  const parts: string[] = [];
  if (error.reason && error.reason !== "unknown") parts.push(error.reason);
  if (error.transportError) parts.push(error.transportError);
  if (error.providerStatus) parts.push(`status ${error.providerStatus}`);
  if (error.providerError) parts.push(error.providerError);
  if (error.providerMessage) parts.push(`"${error.providerMessage}"`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

// SKE-3453 — loading skeleton with model-aware progress + rotating copy.
//
// Contract — what the reader sees while we're waiting on OpenRouter:
//   1. A thin progress bar under the eyebrow, sized off the active
//      model's p95 latency. The bar asymptotes at 90% — it never
//      reaches 100% because that would signal "done" before we are.
//   2. Rotating interim copy every 4 seconds ("Reading your traffic
//      mix…" → "Comparing platforms and channels…" → "Ranking this
//      week's actions…" → "Writing the briefing…"). Deterministic by
//      elapsed time so screenshots reproduce and the text doesn't
//      re-shuffle on unrelated React re-renders.
//   3. A muted `mm:ss` elapsed counter. No countdown — a model
//      exceeding its p95 is precisely the case where a countdown
//      would mislead, so we stick to truthful elapsed time.
//   4. A generic slow hint once elapsed > 10s. Replaces the "still
//      working" message with an honest "AI analysis usually finishes
//      in ~10s — running long, we'll switch models if we have to."
//      Flat 10s rather than per-model p95 × 1.5 because percentile
//      arithmetic on sub-second p50s rounds to a misleading "~1s"
//      the end-to-end path (KV + fallback hops + network) doesn't
//      actually hit.
//
// Accessibility:
//   - The outer container keeps `aria-busy="true"` so assistive tech
//     announces the region as busy exactly once on mount.
//   - `aria-live` is NOT set on the rotating copy, so the four phases
//     don't get re-announced every 4 seconds (that would be noise).
//   - The slow-hint IS inside an `aria-live="polite"` region because it
//     represents a material change in state worth announcing.
//   - The progress bar carries `role="progressbar"` and the
//     `aria-valuenow` / `aria-valuemin` / `aria-valuemax` triplet so
//     assistive tech can speak a percentage on demand.
function InsightsSkeleton({ modelId }: { modelId: string }) {
  const stats = getModelLatencyStats(modelId);
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    // 200ms tick — responsive enough for the progress bar to feel alive
    // without burning the battery on a 16ms rAF loop.
    const startedAt = Date.now();
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 200);
    return () => window.clearInterval(id);
  }, []);

  const progressPct = Math.min(0.9, elapsedMs / stats.p95);
  const phaseIdx = Math.min(SKELETON_PHASES.length - 1, Math.floor(elapsedMs / SKELETON_PHASE_MS));
  // SKE-3468 — flat 10s threshold + model-agnostic copy. The per-model
  // percentile hint ("Grok usually finishes in ~1s") overclaimed on
  // speed (p50=713ms rounded to "1s" set a sub-second expectation that
  // a network-bound LLM call with KV + fallback hops rarely meets) and
  // leaked model names that are noise to the user. A single honest
  // baseline matches the real-world path for most users.
  const SLOW_HINT_THRESHOLD_MS = 10_000;
  const slowHintVisible = elapsedMs > SLOW_HINT_THRESHOLD_MS;
  const slowHintCopy =
    "AI analysis usually finishes in about 10 seconds. This one is running long, so we may switch to another model.";

  return (
    <div aria-busy="true" className="space-y-4">
      {/* Progress bar + rotating copy + elapsed counter. A thin 2px rail
          with a blurple fill so it reads as "making progress" without
          drawing the eye away from the main skeleton. */}
      <div className="space-y-1.5">
        <div
          role="progressbar"
          aria-valuenow={Math.round(progressPct * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Generating AI insights"
          className="h-[3px] w-full overflow-hidden rounded-full bg-neutral-100"
        >
          <div
            className="h-full bg-purple-600 transition-[width] duration-200 ease-linear"
            style={{ width: `${Math.round(progressPct * 100)}%` }}
          />
        </div>
        {/* Interim copy + elapsed counter. Intentionally outside any
            aria-live region — the phase transitions are decoration, not
            material state changes. */}
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <span className="inline-flex items-center gap-1.5">
            <i className="fa-regular fa-sparkles text-purple-600" aria-hidden="true" />
            <span>{SKELETON_PHASES[phaseIdx]}</span>
          </span>
          <span aria-hidden="true" className="font-mono tabular-nums text-[11px] text-neutral-400">
            {formatElapsed(elapsedMs)}
          </span>
        </div>
      </div>

      {/* Shimmer placeholder for the headline + preview bullets + action
          cards. Visually matches the populated layout's rhythm so the
          reveal animation is minimal. */}
      <div className="h-5 w-[65%] animate-pulse rounded-md bg-neutral-100" />
      <div className="space-y-2">
        <div className="h-4 w-[88%] animate-pulse rounded-md bg-neutral-100" />
        <div className="h-4 w-[72%] animate-pulse rounded-md bg-neutral-100" />
      </div>
      <ul className="mt-3 space-y-2">
        {[0, 1, 2].map((i) => (
          <li
            key={i}
            className="h-20 animate-pulse rounded-2xl bg-neutral-50 ring-1 ring-neutral-100"
          />
        ))}
      </ul>

      {/* Slow-hint lives inside an aria-live region because it IS a
          material state change ("we went from 'expected' to 'slow'").
          Screen readers will announce it once when it flips visible;
          they won't re-announce because the DOM doesn't change after
          that until the request completes. */}
      <div aria-live="polite" className="min-h-[1.5rem]">
        {slowHintVisible ? (
          <p className="text-xs text-amber-700">
            <i className="fa-regular fa-clock mr-1.5" aria-hidden="true" />
            {slowHintCopy}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// SKE-3450 — "Ledger" editorial layout. Drawn from schedugram-client's
// `.interface-design/system.md` (editor's desk, Gotham-only, hairline
// rulings, tabular-nums for every metric). Preview bullets are numbered
// `01/02/03`; actions render as a tabular grid (`# · Action · Metric`)
// with a subtle background tint on row 01 (Phase 2 replaced the original
// blurple left-rail). Tone reads as ink-only small caps — no status pills.

function toneLabel(tone: InsightsAction["tone"]): string {
  switch (tone) {
    case "risk":
      return "Risk";
    case "momentum":
      return "Momentum";
    case "hygiene":
    default:
      return "Hygiene";
  }
}

function toneWireColor(tone: InsightsAction["tone"]): string {
  switch (tone) {
    case "risk":
      return "text-red-700";
    case "momentum":
      return "text-emerald-700";
    case "hygiene":
    default:
      return "text-neutral-500";
  }
}

function InsightsLedger({
  insightsV2,
  startDate,
  endDate,
}: {
  insightsV2: InsightsV2;
  startDate?: string;
  endDate?: string;
}) {
  const { headline, summary, actions } = insightsV2;
  const hasExpanded = summary.expandedParagraphs.length > 0;
  // Derive the briefing eyebrow from the report range so a 30- or 90-day
  // view doesn't read "Weekly". Falls back to a generic label when the
  // range isn't available (e.g. legacy share envelopes without dates).
  const eyebrow =
    startDate && endDate
      ? `${briefingPeriod(startDate, endDate)} briefing · Signal`
      : "AI briefing · Signal";
  return (
    <article className="rounded bg-white p-6 ring-1 ring-neutral-950/10">
      <header className="border-b border-neutral-950/10 pb-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
          {eyebrow}
        </p>
        <h2 className="mt-2 max-w-[52ch] text-[1.375rem] font-semibold leading-snug tracking-tight text-balance text-neutral-950">
          {headline}
        </h2>
      </header>
      {summary.previewBullets.length > 0 ? (
        // No bottom rule here — the "See more detail" disclosure below
        // is semantically part of the briefing (it IS the preview's
        // expansion), so they should read as one block without a
        // separator between them.
        <div className="pt-4">
          <ul role="list" className="space-y-2 text-[14px] text-neutral-800 tabular-nums">
            {summary.previewBullets.map((bullet, i) => (
              <li key={i} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className="min-w-[1.25rem] font-mono text-[12px] text-neutral-400 tabular-nums"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-pretty leading-relaxed">{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hasExpanded ? (
        // Bottom rule stays here so the briefing-block (preview + "See
        // more detail") is visually delimited from the Actions ledger
        // below.
        <div className="border-b border-neutral-950/10 pt-3 pb-4">
          <InsightsDisclosure paragraphs={summary.expandedParagraphs} />
        </div>
      ) : null}
      {actions.length > 0 ? (
        <>
          <div className="mt-4 mb-2 grid grid-cols-[auto_minmax(0,1fr)_auto] gap-x-4 px-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400">
            <span>#</span>
            <span>Actions</span>
            <span className="text-right">Metric</span>
          </div>
          <ol role="list" className="divide-y divide-neutral-950/5">
            {actions.map((action, idx) => (
              <LedgerActionRow
                key={action.priority}
                action={action}
                index={idx}
                primary={idx === 0}
              />
            ))}
          </ol>
        </>
      ) : null}
    </article>
  );
}

function LedgerActionRow({
  action,
  index,
  primary,
}: {
  action: InsightsAction;
  index: number;
  primary: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();
  const metadata: string[] = [];
  if (action.platform) metadata.push(action.platform);
  if (action.channel && action.channel !== action.platform) metadata.push(action.channel);
  if (action.landingPage) metadata.push(action.landingPage);
  if (action.utmTag) metadata.push(action.utmTag);
  const hasExpandedBody = Boolean(action.evidence) || metadata.length > 0 || Boolean(action.delta);
  return (
    <li
      className={cn(
        "px-1 py-4",
        // Phase 2 — emphasise the priority-1 row with a subtle full
        // background tint instead of a colored left-border (the
        // side-stripe accent is a banned AI tell). The leading "01"
        // number + tint carry the emphasis now.
        primary && "-mx-1 rounded-md bg-purple-50/40 px-2",
      )}
    >
      {/* Collapsed header — always visible. Clickable when the row has
          any expandable content; renders as a plain row otherwise so we
          don't advertise a disclosure that does nothing. The header
          mirrors the ledger's grid (`# · title · chevron`) so column
          alignment holds whether a row is open or closed. */}
      <button
        type="button"
        // Only advertise expand/collapse semantics when the row actually
        // has an expandable panel. A disabled button with aria-expanded
        // announces "collapsed" to AT even though nothing will open.
        aria-expanded={hasExpandedBody ? expanded : undefined}
        aria-controls={hasExpandedBody ? panelId : undefined}
        onClick={hasExpandedBody ? () => setExpanded((v) => !v) : undefined}
        disabled={!hasExpandedBody}
        className={cn(
          "grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-4 text-left",
          hasExpandedBody &&
            "cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2",
          !hasExpandedBody && "cursor-default",
        )}
      >
        <span className="mt-0.5 font-mono text-[12px] tabular-nums text-neutral-400">
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="min-w-0">
          <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span
              className={cn(
                "text-[10px] font-semibold uppercase tracking-[0.14em]",
                toneWireColor(action.tone),
              )}
            >
              {toneLabel(action.tone)} · {action.label}
            </span>
            {action.lowSample ? (
              <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-neutral-400">
                Low sample
              </span>
            ) : null}
          </p>
          <h3 className="mt-1 text-[15px] font-semibold leading-snug text-neutral-950 text-balance">
            {action.title}
          </h3>
        </div>
        {/* Chevron sits in the Metric column slot so the ledger's
            three-column grid stays aligned row-to-row. When the body
            expands, the chevron rotates and the metric value
            reappears beneath it.

            `min-w-[5rem]` matches the metric column's width on the
            expanded body's grid below — both grids' `auto` third column
            therefore resolve to the same 80px, keeping the middle
            text column aligned vertically across open/closed states.
            Without this lock the chevron's intrinsic ~16px width let
            the title shift right when collapsed vs the evidence text
            below when expanded. */}
        <span className="flex min-w-[5rem] justify-end">
          {hasExpandedBody ? (
            <i
              aria-hidden="true"
              className={cn(
                "fa-solid fa-chevron-down mt-1 text-[11px] text-neutral-400 transition-transform",
                expanded && "rotate-180",
              )}
            />
          ) : null}
        </span>
      </button>

      {/* Expanded body — evidence, metadata, delta/confidence. Lives in
          the same grid so the columns line up with the collapsed
          header above. `hidden` keeps the DOM small for unexpanded
          rows (no layout cost beyond one flex button per action). */}
      <div
        id={panelId}
        hidden={!expanded || !hasExpandedBody}
        className="mt-2 grid grid-cols-[auto_minmax(0,1fr)_auto] gap-x-4"
      >
        <span aria-hidden="true" /> {/* spacer under priority number */}
        <div className="min-w-0">
          {action.evidence ? (
            <p className="text-[13px] leading-relaxed text-neutral-700 text-pretty">
              {action.evidence}
            </p>
          ) : null}
          {/* `action.action` is the concrete recommendation text — distinct
              from `title` (problem headline) and `evidence` (supporting
              data). Only render when it says something new; otherwise the
              title/evidence already carry the message. */}
          {action.action.trim() &&
          action.action.trim() !== action.title.trim() &&
          action.action.trim() !== action.evidence.trim() ? (
            <p className="mt-1.5 text-[13px] leading-relaxed text-neutral-800 text-pretty">
              {action.action}
            </p>
          ) : null}
          {metadata.length > 0 ? (
            <p className="mt-1.5 text-[12px] text-neutral-500">{metadata.join(" · ")}</p>
          ) : null}
        </div>
        <div className="flex min-w-[5rem] flex-col items-end gap-1 text-right">
          {/* Metric name above the delta so the value has a label — the
              column header "Metric" is what this labels. */}
          <p className="text-[10px] uppercase tracking-[0.12em] text-neutral-400">
            {action.metric}
          </p>
          {action.delta ? (
            <p className="font-mono text-[15px] font-medium tabular-nums text-neutral-950">
              {action.delta}
            </p>
          ) : null}
          <p className="text-[10px] uppercase tracking-[0.12em] text-neutral-400">
            {action.confidence}
          </p>
        </div>
      </div>
    </li>
  );
}

interface InsightsErrorPanelProps {
  title: string;
  message: string;
  /** One-line technical detail (reason + status + provider code). Muted
   *  row under the message. Null = suppress. */
  detail?: string | null;
  /** parse_failure only — first ~280 chars of raw LLM output.
   *  Rendered inside a <details> so power users can see what actually
   *  came back when the parser couldn't structure it. */
  rawPreview?: string | null;
  onRetry: (() => void) | null;
}

function InsightsErrorPanel({
  title,
  message,
  detail,
  rawPreview,
  onRetry,
}: InsightsErrorPanelProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <i
          className="fa-regular fa-circle-info mt-0.5 text-base text-amber-500"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-semibold text-neutral-950">{title}</p>
          <p className="mt-1 text-sm text-neutral-600">{message}</p>
          {detail ? (
            <p className="mt-2 font-mono text-[11px] uppercase tracking-wide text-neutral-400">
              {detail}
            </p>
          ) : null}
          {rawPreview ? (
            <details className="mt-3 text-xs text-neutral-600">
              <summary className="cursor-pointer select-none text-purple-700 hover:underline">
                Show raw AI output
              </summary>
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-neutral-50 p-3 font-mono text-[11px] leading-relaxed text-neutral-700 ring-1 ring-neutral-200">
                {rawPreview}
              </pre>
            </details>
          ) : null}
        </div>
      </div>
      {onRetry ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRetry}
          className="text-purple-700"
        >
          <i className="fa-solid fa-rotate-right text-sm" aria-hidden="true" />
          Try again
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FrozenInsightsSection — rendered on /share/:token (public viewer).
//
// Two render paths, prefer-V2:
//
//   1. `insightsV2` present (v4 envelope, SKE-3458): render the full
//      structured layout the dashboard uses — headline + preview
//      bullets + "See more detail" disclosure + tabular action rows.
//      Uses the same `InsightsLedger` component as the live dashboard
//      so the two views stay visually identical.
//   2. `insightsV2` absent but `insights` present (v3 envelope): fall
//      back to the legacy paragraph + bullets render. Keeps pre-v4
//      shares rendering correctly until they expire.
//   3. Both absent / empty: render nothing (v1/v2 envelopes + the
//      failure-at-mint case).
//
// No regenerate button, no model picker — this is a frozen snapshot.
// ---------------------------------------------------------------------------

export interface FrozenInsightsSectionProps {
  insights: InsightsResult | null | undefined;
  /** SKE-3458 — V2 structured insights on v4 envelopes. Null / absent
   *  on older envelopes (falls back to `insights` legacy render). */
  insightsV2?: InsightsV2 | null;
  /** Resolved window used to pick the period possessive in the heading
   *  ("this week's" / "this month's" / "this quarter's" / "this
   *  period's"). Optional so legacy share envelopes without these
   *  fields still render — they fall back to the generic phrasing. */
  startDate?: string;
  endDate?: string;
}

export function FrozenInsightsSection({
  insights,
  insightsV2,
  startDate,
  endDate,
}: FrozenInsightsSectionProps) {
  // Truthy-check the values themselves (not derived booleans) so
  // TypeScript can narrow `insightsV2` / `insights` inside each JSX
  // branch without `as`-casts. A `Boolean(insightsV2)` gate would let
  // a future caller slip a `{}` past and crash inside `InsightsLedger`'s
  // destructure — value-level narrowing catches that at the call site.
  const hasLegacy =
    insights !== null &&
    insights !== undefined &&
    (insights.paragraph.length > 0 || insights.bullets.length > 0);
  if (!insightsV2 && !hasLegacy) return null;
  const heading = startDate && endDate ? periodPossessive(startDate, endDate) : "this period's";
  return (
    <section id="insights" className="scroll-mt-24 space-y-4">
      <SectionHeader
        eyebrow="AI insights"
        title={
          <>
            What {heading} data is <em className="font-serif text-purple-700">telling you</em>
          </>
        }
      />
      {/* SKE-3450 — prefer the Ledger layout (identical to the owner's
          dashboard view) when v4 envelopes carry V2 insights. The
          Ledger renders its own chrome, so it's a sibling of the
          legacy-wrapper div rather than living inside it. */}
      {insightsV2 ? (
        <InsightsLedger insightsV2={insightsV2} startDate={startDate} endDate={endDate} />
      ) : insights ? (
        <div className="rounded-2xl bg-white p-6 ring-1 ring-neutral-950/5">
          <LegacyInsightsContent insights={insights} />
        </div>
      ) : null}
    </section>
  );
}

/** Split the paragraph body on blank lines so the renderer can emit
 *  one short <p> per sentence — consistent with how M13.6 shipped. */
function splitParagraphs(paragraph: string): string[] {
  return paragraph
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

function LegacyInsightsContent({ insights }: { insights: InsightsResult }) {
  const paragraphs = insights.paragraph ? splitParagraphs(insights.paragraph) : [];
  return (
    <div className="space-y-4">
      {paragraphs.length > 0 ? (
        <div className="space-y-3">
          {paragraphs.map((text, i) => (
            <p key={i} className="text-base leading-relaxed text-neutral-800">
              {text}
            </p>
          ))}
        </div>
      ) : null}
      {insights.bullets.length > 0 ? (
        <ul className="space-y-2 text-sm text-neutral-700">
          {insights.bullets.map((line, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-purple-500"
              />
              <span className="leading-relaxed">{line}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
